<template>
    <card>
        <template #header>
            <h1 class="card-title mb-0">Fax History</h1>
        </template>
        <b-alert :show="!!warning" variant="warning" dismissible @dismissed="warning = ''">
            {{ warning }}
        </b-alert>
        <!-- The filters are locked while an export runs: it pulls the whole filtered set over
             several requests, and editing them mid-run would leave the file describing a
             different result set than the one on screen. -->
        <div class="filter-grid mb-2">
            <DateRangeFilter v-model="dateRange" label="Date Range" :clearable="false" :disabled="exporting" @input="resetAndReload" />
            <fg-input label="Fax Type">
                <b-form-select v-model="filters.faxType" :options="faxTypeOptions" :disabled="exporting" @change="resetAndReload" />
            </fg-input>
            <fg-input label="Status">
                <b-form-select v-model="filters.status" :options="statusOptions" :disabled="exporting" @change="resetAndReload" />
            </fg-input>
            <fg-input label="Sender">
                <b-form-input v-model="filters.createdBy" debounce="400" placeholder="Username" :disabled="exporting" @update="resetAndReload" />
            </fg-input>
            <fg-input label="Recipient Fax #">
                <b-form-input v-model="filters.faxNumber" debounce="400" placeholder="e.g. 5551234567" :disabled="exporting" @update="resetAndReload" />
            </fg-input>
            <div class="d-flex align-items-end">
                <b-button variant="outline-secondary" size="sm" class="mb-3" :disabled="exporting" @click="clearFilters">Clear</b-button>
            </div>
        </div>
        <b-table id="fax-history-table"
                 ref="fax-history-table"
                 outlined
                 show-empty
                 empty-text="No faxes found for the selected filters."
                 :busy="loading"
                 :fields="fields"
                 :items="rows">
            <template #table-busy>
                <div class="text-center my-2">
                    <b-spinner class="align-middle mr-2"></b-spinner>
                    <strong>Loading...</strong>
                </div>
            </template>
            <template #cell(created)="row">{{ formatDate(row.item.created) }}</template>
            <template #cell(faxType)="row">{{ formatFaxType(row.item.faxType) }}</template>
            <template #cell(status)="row">
                <b-badge :variant="statusVariant(row.item.status)"
                         v-b-tooltip.hover.right
                         :title="row.item.errorInfo || ''"
                         :style="row.item.errorInfo ? 'cursor: help;' : ''">
                    {{ row.item.status || '—' }}
                </b-badge>
            </template>
            <template #cell(resolved)="row">
                <span v-if="row.item.resolvedDate">{{ formatDate(row.item.resolvedDate) }}</span>
                <span v-else class="text-muted">—</span>
            </template>
            <template #cell(linkedEntity)="row">
                <span v-if="row.item.entityType !== null && row.item.entityType !== undefined">
                    {{ entityTypeName(row.item.entityType) }}<span v-if="row.item.entityId">: {{ row.item.entityId }}</span>
                </span>
                <span v-else>—</span>
            </template>
            <template #cell(image)="row">
                <b-button v-if="row.item.imageId" variant="outline-primary" size="sm"
                          @click="viewImage(row.item)" v-b-tooltip.hover title="View document">
                    <b-icon-file-earmark-text />
                </b-button>
                <span v-else class="text-muted">—</span>
            </template>
        </b-table>
        <BottomPagerBar :value="pageNumber" :total-rows="totalCount" :per-page="pageSize" show-total
                        show-page-size-selector item-name="fax" :loading="loading" no-stick
                        @input="onPageChange" @change-page-size="onPageSizeChange">
            <template #left-after>
                <b-button @click="reload" variant="primary" size="sm" class="m-0">
                    <b-icon-arrow-clockwise /> Refresh
                </b-button>
                <!-- Drops up: the pager bar sits at the bottom of the page, so a down menu
                     would open off-screen. -->
                <ExportDropdown dropup size="sm" class="m-0" :disabled="loading || exporting || !totalCount"
                                @export-pdf="savePDF" @export-csv="saveCSV" />
                <span v-if="exporting">
                    <b-spinner small class="align-middle" />
                    <strong>Exporting...</strong>
                </span>
            </template>
        </BottomPagerBar>
        <b-modal v-model="imageModal.show" size="lg" :title="`Fax document #${imageModal.faxId ?? ''}`"
                 hide-footer @hidden="closeImageModal">
            <div v-if="imageModal.loading" class="text-center my-4">
                <b-spinner /> <strong class="ml-2">Loading document...</strong>
            </div>
            <b-alert :show="!!imageModal.error" variant="danger">{{ imageModal.error }}</b-alert>
            <iframe v-if="imageModal.url" :src="imageModal.url" style="width:100%;height:75vh;border:0;" />
        </b-modal>
    </card>
</template>
<script lang="ts">
import { Component, Mixins } from "vue-property-decorator";
import { BvTableFieldArray } from "bootstrap-vue";
import moment from "moment/moment";
import axios from "axios";
import BottomPagerBar from "@/components/BottomPagerBar.vue";
import DateRangeFilter from "@/components/DateRangeFilter.vue";
import ExportDropdown from "@/components/ExportDropdown.vue";
import { ExportField, ReportExporter } from "@/mixins/ReportExporter";

interface FaxRow {
    id: number;
    entityType: number | null;
    entityId: string | null;
    faxNumber: string | null;
    created: string | null;
    createdBy: string | null;
    imageId: number | null;
    note: string | null;
    faxType: number | null;
    status: string | null;
    mFaxMessageId: string | null;
    mFaxDeliveryId: string | null;
    resolvedDate: string | null;
    errorInfo: string | null;
    totalCount: number;
}

const FAX_TYPE_LABELS: Record<number, string> = {
    0: "Rx External Transfer",
    1: "Refill Authorization",
    2: "Rx Clarification",
    3: "New Rx Request",
    4: "Unfilled NDC",
    5: "Alternate Rx",
    6: "Rx Transfer In",
    7: "Escript Rx Renewal Request",
    8: "Custom Prescriber Template",
    9: "Custom Prior Auth Template",
    10: "User Task Completion Form",
    11: "User Task Summary",
};

const ENTITY_TYPE_LABELS: Record<number, string> = {
    0: "Unknown", 1: "Rx Hardcopy", 2: "Patient", 3: "Case", 4: "Program", 5: "Prior Auth",
    6: "Dispense Error", 7: "Rx Transfer", 8: "Prescriber", 9: "Follow-Up Case",
    10: "Electronic PA", 16: "Plan", 18: "User Task", 19: "Order", 21: "Image",
    27: "Prescription", 28: "Store", 31: "Fax",
};

@Component({
    name: "FaxSentHistoryPage",
    components: { BottomPagerBar, DateRangeFilter, ExportDropdown },
})
export default class FaxSentHistoryPage extends Mixins(ReportExporter) {
    dateRange: { startDate: any, endDate: any } = {
        startDate: moment().subtract(7, "days").startOf("day").toDate(),
        endDate: moment().endOf("day").toDate(),
    };
    filters = {
        faxType: null as number | null,
        status: null as string | null,
        createdBy: "",
        faxNumber: "",
    };
    rows: FaxRow[] = [];
    loading = false;
    exporting = false;
    warning = "";
    pageNumber = 1;
    pageSize = 50;
    totalCount = 0;
    imageModal: { show: boolean; faxId: number | null; url: string | null; loading: boolean; error: string } = {
        show: false, faxId: null, url: null, loading: false, error: "",
    };

    private abortController: AbortController | null = null;
    // Exporting the same filters as both CSV and PDF is routine, and the fetch below can run
    // to dozens of requests, so the last full result set is kept until the filters change.
    private exportCache: { signature: string; rows: FaxRow[] } | null = null;
    // Bumped on every reload so an export that was already in flight can tell its cached
    // result is no longer the freshest thing the page has asked for.
    private exportGeneration = 0;

    mounted() { this.reload(); }

    get faxTypeOptions() {
        const opts = Object.entries(FAX_TYPE_LABELS).map(([v, t]) => ({ value: Number(v), text: t }));
        return [{ value: null, text: "All types" }, ...opts];
    }

    get statusOptions() {
        return [
            { value: null, text: "All statuses" },
            { value: "Sending", text: "Sending" },
            { value: "Sent", text: "Sent" },
            { value: "Failed", text: "Failed" },
            { value: "Processing", text: "Processing" },
        ];
    }

    get fields(): BvTableFieldArray {
        return [
            { key: "created", label: "Sent", sortable: false },
            { key: "faxType", label: "Type" },
            { key: "faxNumber", label: "Recipient" },
            { key: "createdBy", label: "Sender" },
            { key: "status", label: "Status" },
            { key: "resolved", label: "Resolved" },
            { key: "mFaxMessageId", label: "mFax ID" },
            { key: "linkedEntity", label: "Linked Entity" },
            { key: "image", label: "Doc", class: "text-center" },
        ];
    }

    resetAndReload() {
        this.pageNumber = 1;
        return this.reload();
    }

    onPageChange(newPage: number) {
        this.pageNumber = newPage;
        this.reload();
    }

    onPageSizeChange(newSize: number) {
        this.pageSize = newSize;
        this.pageNumber = 1;
        this.reload();
    }

    private buildFilterParams(): Record<string, any> {
        // Use ISO with time so the SP date filter sees a real end-of-day boundary;
        // the SP also defends with a half-open range in case the time gets stripped.
        const params: Record<string, any> = {
            fromDate: moment(this.dateRange.startDate).startOf("day").toISOString(),
            toDate: moment(this.dateRange.endDate).endOf("day").toISOString(),
        };
        if (this.filters.faxType !== null) params.faxType = this.filters.faxType;
        if (this.filters.status) params.status = this.filters.status;
        if (this.filters.createdBy) params.createdBy = this.filters.createdBy;
        if (this.filters.faxNumber) params.faxNumber = this.filters.faxNumber;
        return params;
    }

    // A deliberate refresh should also re-pull the export set, not just the visible page. The
    // bump travels with the clear so a later third caller cannot drop one and keep the other.
    private invalidateExportCache() {
        this.exportCache = null;
        this.exportGeneration++;
    }

    async reload() {
        this.loading = true;
        this.warning = "";
        this.invalidateExportCache();
        this.abortController?.abort();
        this.abortController = new AbortController();

        try {
            const resp = await axios.get<{ totalCount: number; rows: FaxRow[] }>("/api/Fax/sent-history", {
                params: {
                    ...this.buildFilterParams(),
                    offset: (this.pageNumber - 1) * this.pageSize,
                    limit: this.pageSize,
                },
                signal: this.abortController.signal,
            });
            this.rows = resp.data.rows ?? [];
            this.totalCount = resp.data.totalCount ?? 0;
        } catch (err: any) {
            if (err.name === "CanceledError" || err.name === "AbortError") return;
            this.warning = err?.response?.data ?? "Failed to load fax history.";
            this.rows = [];
            this.totalCount = 0;
        } finally {
            this.loading = false;
        }
    }

    // The endpoint caps limit at 500 per request, so the full filtered set is fetched in batches.
    // The filters are snapshotted before the first request: they stay editable while the export
    // runs, and re-reading them per batch would splice two different result sets into one file.
    private async fetchAllRows(): Promise<FaxRow[]> {
        const baseParams = this.buildFilterParams();
        const signature = JSON.stringify(baseParams);
        if (this.exportCache?.signature === signature) return this.exportCache.rows;

        const generation = this.exportGeneration;
        const batchSize = 500;
        const all: FaxRow[] = [];
        let expectedTotal = Infinity;
        while (all.length < expectedTotal) {
            const resp = await axios.get<{ totalCount: number; rows: FaxRow[] }>("/api/Fax/sent-history", {
                params: { ...baseParams, offset: all.length, limit: batchSize },
            });
            const rows = resp.data.rows ?? [];
            expectedTotal = resp.data.totalCount ?? 0;
            all.push(...rows);
            if (!rows.length) break;
        }
        // A reload during the fetch already cleared the cache on purpose; don't refill it behind that.
        if (generation === this.exportGeneration) this.exportCache = { signature, rows: all };
        return all;
    }

    get exportFields(): ExportField[] {
        return [
            { key: "created", label: "Sent", formatter: this.formatDate },
            // The screen's em-dash placeholder is a display glyph; in a CSV it would parse as
            // data, so an absent type exports empty like the other nullable columns. The mixin
            // has already collapsed a missing value to '' by the time a formatter sees it.
            { key: "faxType", label: "Type", formatter: (t: any) => (t === "" ? "" : this.formatFaxType(t)) },
            { key: "faxNumber", label: "Recipient" },
            { key: "createdBy", label: "Sender" },
            { key: "status", label: "Status" },
            { key: "resolvedDate", label: "Resolved", formatter: this.formatDate },
            { key: "mFaxMessageId", label: "mFax ID" },
            {
                key: "entityType", label: "Linked Entity",
                formatter: (et: any, _k?: string, item?: any) => et === null || et === undefined || et === ""
                    ? ""
                    : `${this.entityTypeName(et)}${item?.entityId ? `: ${item.entityId}` : ""}`
            },
        ];
    }

    // Only the fetch can surface here — the mixin reports failures from the export itself, so
    // this banner would be a second, differently-worded account of the same problem.
    private async exportAll(exporter: (rows: FaxRow[]) => void | Promise<void>) {
        this.exporting = true;
        this.warning = "";
        try {
            await exporter(await this.fetchAllRows());
        } catch (err: any) {
            this.warning = err?.response?.data ?? "Failed to load the full fax history to export.";
        } finally {
            this.exporting = false;
        }
    }

    savePDF() {
        return this.exportAll(rows => this.ExportDataPDF("Fax History", rows, this.exportFields));
    }

    saveCSV() {
        return this.exportAll(rows => this.ExportCSV("Fax History", rows, this.exportFields));
    }

    clearFilters() {
        this.filters = { faxType: null, status: null, createdBy: "", faxNumber: "" };
        this.pageNumber = 1;
        this.reload();
    }

    // Created/ResolvedDate are GETUTCDATE() stamps in offset-less DATETIME columns, so they serialize
    // without a "Z" — parse as UTC explicitly or moment reads them in the viewer's frame.
    formatDate(d: string | null): string { return d ? moment.utc(d).local().format("L LT") : ""; }
    formatFaxType(t: number | null): string {
        return t === null || t === undefined ? "—" : (FAX_TYPE_LABELS[t] ?? `#${t}`);
    }
    entityTypeName(et: number): string { return ENTITY_TYPE_LABELS[et] ?? `#${et}`; }

    statusVariant(status: string | null): string {
        switch (status) {
            case "Sent": return "success";
            case "Failed": return "danger";
            case "Sending":
            case "Processing": return "info";
            default: return "secondary";
        }
    }

    async viewImage(row: FaxRow) {
        if (!row.imageId) return;
        this.imageModal = { show: true, faxId: row.id, url: null, loading: true, error: "" };
        try {
            const resp = await axios.get(`/api/Image/${row.imageId}`, { responseType: "blob" });
            this.imageModal.url = URL.createObjectURL(resp.data);
        } catch (err: any) {
            this.imageModal.error = err?.response?.data ?? "Failed to load document.";
        } finally {
            this.imageModal.loading = false;
        }
    }

    closeImageModal() {
        if (this.imageModal.url) URL.revokeObjectURL(this.imageModal.url);
        this.imageModal = { show: false, faxId: null, url: null, loading: false, error: "" };
    }
}
</script>
<style scoped>
.filter-grid {
    display: grid;
    gap: 15px;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 330px), 1fr));
}
</style>
